import { useState, useEffect, useContext } from 'react';
import axios from 'api/axiosInstance';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useCompany } from 'contexts/CompanyContext';
// material-ui
import {
  Button,
  FormHelperText,
  Grid,
  InputLabel,
  OutlinedInput,
  Stack,
  Switch,
  Typography,
  TextField,
  MenuItem,
  Select,
  FormControl,
  Modal,
  Box,
  Divider,
  CircularProgress,
  Chip
} from '@mui/material';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import AnimateButton from 'components/@extended/AnimateButton';
import { useSnackbar } from 'contexts/SnackbarContext';
import endpoints from 'api/endpoints';

// Format time to 12-hour format with AM/PM
const formatTimeWithAmPm = (time24) => {
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

// Generate 15-minute interval time slots
const generateTimeSlots = () => {
  const slots = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const h = hour.toString().padStart(2, '0');
      const m = minute.toString().padStart(2, '0');
      slots.push({ value: `${h}:${m}`, label: formatTimeWithAmPm(`${h}:${m}`) });
    }
  }
  return slots;
};

const timeSlots = generateTimeSlots();

// Slot duration options
const slotDurationOptions = [
  { value: 10, label: '10 Minutes' },
  { value: 15, label: '15 Minutes' },
  { value: 30, label: '30 Minutes' },
  { value: 45, label: '45 Minutes' },
  { value: 60, label: '1 Hour' }
];

// Days of the week
const daysOfWeek = [
  { value: 0, label: 'Monday' },
  { value: 1, label: 'Tuesday' },
  { value: 2, label: 'Wednesday' },
  { value: 3, label: 'Thursday' },
  { value: 4, label: 'Friday' },
  { value: 5, label: 'Saturday' },
  { value: 6, label: 'Sunday' }
];

// Default selected days (all days)
const DEFAULT_DAYS = [0, 1, 2, 3, 4, 5, 6];

// Modal style
const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 3
};

export default function DoctorRegister({ onSuccess }) {
  const { showSnackbar } = useSnackbar();
      const { setList } = useCompany();
  const [registering, setRegistering] = useState(false);
  const [specializations, setSpecializations] = useState([]);
  const [loadingSpecializations, setLoadingSpecializations] = useState(false);

  // Create specialization modal state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newSpecName, setNewSpecName] = useState('');
  const [creatingSpec, setCreatingSpec] = useState(false);

  useEffect(() => {
    fetchSpecializations();
  }, []);

  const fetchSpecializations = async () => {
    try {
      setLoadingSpecializations(true);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      const response = await axios.get(endpoints.company.specializations(tenantId), {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      // Handle different response structures
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data.results) {
        data = response.data.results;
      } else if (response.data.data) {
        data = response.data.data;
      }

      setSpecializations(data);
    } catch (error) {
      console.error('Error fetching specializations:', error);
      showSnackbar('Failed to load specializations', 'error');
    } finally {
      setLoadingSpecializations(false);
    }
  };

  const handleCreateSpecialization = async (setFieldValue) => {
    if (!newSpecName.trim()) {
      showSnackbar('Please enter a specialization name', 'warning');
      return;
    }

    try {
      setCreatingSpec(true);
      const token = localStorage.getItem('token');
      const companyId = Number(localStorage.getItem('tenant_id'));

      const payload = {
        name: newSpecName.trim(),
        company: companyId
      };

      const response = await axios.post(endpoints.company.createSpecialization, payload, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      showSnackbar('Specialization created successfully!', 'success');

      // Refresh specializations list
      await fetchSpecializations();

      // Auto-select the newly created specialization
      if (response.data?.id) {
        setFieldValue('specialization', response.data.id);
      }

      // Close modal and reset
      setCreateModalOpen(false);
      setNewSpecName('');
    } catch (error) {
      console.error('Error creating specialization:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.name?.[0] || 'Failed to create specialization';
      showSnackbar(errorMessage, 'error');
    } finally {
      setCreatingSpec(false);
    }
  };

  const handleRegister = async (values, { setErrors, setStatus, setSubmitting }) => {
    try {
      setRegistering(true);
      const token = localStorage.getItem('token');

      const payload = {
        name: values.name,
        specialization: values.specialization,
        email: values.email,
        phone: values.phone || '',
        company: Number(localStorage.getItem('tenant_id')),
        available_from: values.available_from,
        available_to: values.available_to,
        is_active: values.is_active
      };

      console.log('Submitting Doctor Payload:', payload);

      // First, create the doctor
      const response = await axios.post(endpoints?.company?.doctorList(), payload, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      const doctorId = response.data?.id;
      console.log('Doctor Added:', response.data);

      // Calculate off_days (days not selected from DEFAULT_DAYS)
      const offDays = DEFAULT_DAYS.filter(day => !values.selectedDays.includes(day));

      // Now call the doctor availability API
      if (doctorId) {
        const availabilityPayload = {
          days: DEFAULT_DAYS,
          off_days: offDays,
          start_time: values.available_from,
          end_time: values.available_to,
          slot_duration: values.slot_duration,
          day_of_week:0
        };

        console.log('Submitting Availability Payload:', availabilityPayload);

        await axios.post(endpoints.company.doctorAvailability(doctorId), availabilityPayload, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        });

        console.log('Doctor availability set successfully');
      }

      // Refresh doctor list
      const responseOfDoc = await axios.get(endpoints.company.doctorList(), {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      setList(responseOfDoc.data || []);

      showSnackbar('Doctor registered successfully!', 'success');
      setStatus({ success: true });
      setSubmitting(false);
      if (onSuccess) onSuccess({ id: doctorId, name: values.name });
    } catch (error) {
      console.error('Doctor registration error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to register doctor.';
      setStatus({ success: false });
      setErrors({ submit: errorMessage });
      showSnackbar(errorMessage, 'error');
      setSubmitting(false);
    } finally {
      setRegistering(false);
    }
  };

  return (
    <Formik
      initialValues={{
        name: '',
        specialization: '',
        email: '',
        phone: '',
        available_from: '',
        available_to: '',
        slot_duration: 15,
        selectedDays: DEFAULT_DAYS,
        is_active: true,
        submit: null
      }}
      validationSchema={Yup.object().shape({
        name: Yup.string().max(255).required('Doctor Name is required'),
        specialization: Yup.number().required('Specialization is required'),
        email: Yup.string().email('Must be a valid email').required('Email is required'),
        available_from: Yup.string().required('Available from time is required'),
        available_to: Yup.string().required('Available to time is required'),
        slot_duration: Yup.number().required('Slot duration is required'),
        selectedDays: Yup.array().min(1, 'At least one day must be selected')
      })}
      onSubmit={handleRegister}
    >
      {({ errors, handleBlur, handleChange, handleSubmit, touched, values, setFieldValue }) => (
        <form noValidate onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Doctor Name */}
            <Grid item xs={12} md={6}>
              <Stack sx={{ gap: 1 }}>
                <InputLabel htmlFor="name">Doctor Name*</InputLabel>
                <OutlinedInput
                  id="name"
                  name="name"
                  value={values.name}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  placeholder="Enter doctor name"
                  fullWidth
                  error={Boolean(touched.name && errors.name)}
                />
              </Stack>
              {touched.name && errors.name && <FormHelperText error>{errors.name}</FormHelperText>}
            </Grid>

            {/* Specialization */}
            <Grid item xs={12} md={6}>
              <Stack sx={{ gap: 1 }}>
                <InputLabel htmlFor="specialization">Specialization*</InputLabel>
                <FormControl fullWidth error={Boolean(touched.specialization && errors.specialization)}>
                  <Select
                    id="specialization"
                    name="specialization"
                    value={values.specialization}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    displayEmpty
                    disabled={loadingSpecializations}
                  >
                    <MenuItem value="" disabled>
                      {loadingSpecializations ? 'Loading...' : 'Select Specialization'}
                    </MenuItem>
                    {specializations.map((spec) => (
                      <MenuItem key={spec.id} value={spec.id}>
                        {spec.name}
                      </MenuItem>
                    ))}
                    <Divider />
                    <MenuItem
                      onClick={(e) => {
                        e.preventDefault();
                        setCreateModalOpen(true);
                      }}
                      sx={{ color: 'primary.main', fontWeight: 500 }}
                    >
                      <PlusOutlined style={{ marginRight: 8 }} />
                      Create New Specialization
                    </MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              {touched.specialization && errors.specialization && <FormHelperText error>{errors.specialization}</FormHelperText>}

              {/* Create Specialization Modal */}
              <Modal open={createModalOpen} onClose={() => setCreateModalOpen(false)}>
                <Box sx={modalStyle}>
                  <Typography variant="h5" sx={{ mb: 2 }}>
                    Create New Specialization
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      label="Specialization Name"
                      value={newSpecName}
                      onChange={(e) => setNewSpecName(e.target.value)}
                      placeholder="e.g. Cardiologist"
                      size="small"
                    />
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setCreateModalOpen(false);
                          setNewSpecName('');
                        }}
                        disabled={creatingSpec}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() => handleCreateSpecialization(setFieldValue)}
                        disabled={creatingSpec}
                      >
                        {creatingSpec ? <CircularProgress size={20} /> : 'Create'}
                      </Button>
                    </Box>
                  </Stack>
                </Box>
              </Modal>
            </Grid>

            {/* Email */}
            <Grid item xs={12} md={6}>
              <Stack sx={{ gap: 1 }}>
                <InputLabel htmlFor="email">Email*</InputLabel>
                <OutlinedInput
                  id="email"
                  name="email"
                  value={values.email}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  placeholder="doctor@example.com"
                  fullWidth
                  error={Boolean(touched.email && errors.email)}
                />
              </Stack>
              {touched.email && errors.email && <FormHelperText error>{errors.email}</FormHelperText>}
            </Grid>

            {/* Phone (optional) */}
            <Grid item xs={12} md={6}>
              <Stack sx={{ gap: 1 }}>
                <InputLabel htmlFor="phone">Phone</InputLabel>
                <OutlinedInput
                  id="phone"
                  name="phone"
                  value={values.phone}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  placeholder="Optional"
                  fullWidth
                />
              </Stack>
            </Grid>

            {/* Availability */}
            <Grid item xs={12} md={6}>
              <Stack sx={{ gap: 1 }}>
                <InputLabel htmlFor="available_from">Available From*</InputLabel>
                <Select
                  id="available_from"
                  name="available_from"
                  value={values.available_from}
                  onChange={handleChange}
                  fullWidth
                  displayEmpty
                  error={Boolean(touched.available_from && errors.available_from)}
                >
                  <MenuItem value="" disabled>Select time</MenuItem>
                  {timeSlots.map((time) => (
                    <MenuItem key={time.value} value={time.value}>{time.label}</MenuItem>
                  ))}
                </Select>
              </Stack>
              {touched.available_from && errors.available_from && <FormHelperText error>{errors.available_from}</FormHelperText>}
            </Grid>

            <Grid item xs={12} md={6}>
              <Stack sx={{ gap: 1 }}>
                <InputLabel htmlFor="available_to">Available To*</InputLabel>
                <Select
                  id="available_to"
                  name="available_to"
                  value={values.available_to}
                  onChange={handleChange}
                  fullWidth
                  displayEmpty
                  error={Boolean(touched.available_to && errors.available_to)}
                >
                  <MenuItem value="" disabled>Select time</MenuItem>
                  {timeSlots.map((time) => (
                    <MenuItem key={time.value} value={time.value}>{time.label}</MenuItem>
                  ))}
                </Select>
              </Stack>
              {touched.available_to && errors.available_to && <FormHelperText error>{errors.available_to}</FormHelperText>}
            </Grid>

            {/* Slot Duration */}
            <Grid item xs={12} md={6}>
              <Stack sx={{ gap: 1 }}>
                <InputLabel htmlFor="slot_duration">Slot Duration*</InputLabel>
                <Select
                  id="slot_duration"
                  name="slot_duration"
                  value={values.slot_duration}
                  onChange={handleChange}
                  fullWidth
                  error={Boolean(touched.slot_duration && errors.slot_duration)}
                >
                  {slotDurationOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </Stack>
              {touched.slot_duration && errors.slot_duration && <FormHelperText error>{errors.slot_duration}</FormHelperText>}
            </Grid>

            {/* Working Days Selection */}
            <Grid item xs={12}>
              <Stack sx={{ gap: 1 }}>
                <InputLabel>Working Days*</InputLabel>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {daysOfWeek.map((day) => {
                    const isSelected = values.selectedDays.includes(day.value);
                    return (
                      <Chip
                        key={day.value}
                        label={day.label}
                        onClick={() => {
                          const newSelectedDays = isSelected
                            ? values.selectedDays.filter(d => d !== day.value)
                            : [...values.selectedDays, day.value];
                          setFieldValue('selectedDays', newSelectedDays);
                        }}
                        color={isSelected ? 'primary' : 'default'}
                        variant={isSelected ? 'filled' : 'outlined'}
                        sx={{ cursor: 'pointer' }}
                      />
                    );
                  })}
                </Box>
                {touched.selectedDays && errors.selectedDays && <FormHelperText error>{errors.selectedDays}</FormHelperText>}
              </Stack>
            </Grid>

            {/* Active Toggle */}
            <Grid item xs={12}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography>Inactive</Typography>
                <Switch checked={values.is_active} onChange={(e) => setFieldValue('is_active', e.target.checked)} color="primary" />
                <Typography>Active</Typography>
              </Stack>
            </Grid>

            {/* Submit */}
            {errors.submit && (
              <Grid item xs={12}>
                <FormHelperText error>{errors.submit}</FormHelperText>
              </Grid>
            )}

            <Grid item xs={12}>
              <AnimateButton>
                <Button fullWidth size="large" type="submit" variant="contained" color="primary" disabled={registering}>
                  {registering ? 'Submitting...' : 'Register Doctor'}
                </Button>
              </AnimateButton>
            </Grid>
          </Grid>
        </form>
      )}
    </Formik>
  );
}
